import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const copyByLang = {
  es: {
    title: "¿Qué es Puerto Rico Esports League? | PREL",
    description: "Descubre qué es Puerto Rico Esports League (PREL), cómo funciona y por qué se ha convertido en una plataforma de esports y entretenimiento construida desde Puerto Rico.",
    keywords: "qué es Puerto Rico Esports League, PREL, esports Puerto Rico, gaming Puerto Rico, plataforma gaming Puerto Rico",
    ogDescription: "Una explicación clara de qué es PREL, cómo opera y qué papel juega dentro del ecosistema de esports de Puerto Rico.",
    back: "← Volver al inicio",
    eyebrow: "Guía PREL",
    heroTitle: "¿Qué es Puerto Rico Esports League?",
    heroBody:
      "Puerto Rico Esports League, también conocida como PREL, es una plataforma de esports y entretenimiento construida desde Puerto Rico. Su trabajo combina eventos en vivo, contenido original, televisión, activaciones y colaboraciones estratégicas alrededor de la cultura competitiva.",
    shortTitle: "La explicación corta",
    shortBody:
      "PREL no funciona solamente como una liga de torneos. La plataforma también produce contenido, desarrolla formatos audiovisuales, trabaja con marcas e instituciones y crea experiencias que conectan comunidad, competencia y entretenimiento.",
    points: [
      "Plataforma de esports y entretenimiento basada en Puerto Rico",
      "Contenido original, eventos en vivo y programación televisiva",
      "Proyectos con marcas, medios, instituciones y comunidad",
      "Visión de crecimiento desde Puerto Rico hacia mercados regionales e internacionales",
    ],
    sections: [
      {
        title: "Qué hace PREL en la práctica",
        body: "En la práctica, PREL desarrolla torneos, cobertura de eventos, contenido para plataformas digitales, programación tipo televisiva y oportunidades de colaboración para marcas y aliados. Eso le permite moverse entre el entretenimiento, la competencia y la cultura joven sin quedarse atrapado en un solo formato.",
      },
      {
        title: "Por qué importa dentro de Puerto Rico",
        body: "PREL ayuda a darle estructura y visibilidad al ecosistema de esports de Puerto Rico. También funciona como punto de encuentro entre jugadores, audiencias, marcas, medios e instituciones que quieren participar en el crecimiento del sector de manera seria y culturalmente relevante.",
      },
      {
        title: "Por qué no suena como un proyecto genérico",
        body: "PREL tiene una identidad construida desde Puerto Rico. Ese contexto le da tono, lenguaje, comunidad y legitimidad. A la vez, su formato está pensado para crecer y para conversar con oportunidades fuera de la isla sin perder lo que lo hace reconocible.",
      },
    ],
  },
  en: {
    title: "What is Puerto Rico Esports League? | PREL",
    description: "Learn what Puerto Rico Esports League (PREL) is, how it works, and why it has become an esports and entertainment platform built from Puerto Rico.",
    keywords: "what is Puerto Rico Esports League, PREL, Puerto Rico esports, Puerto Rico gaming, Puerto Rico gaming platform",
    ogDescription: "A clear explanation of what PREL is, how it operates, and the role it plays inside Puerto Rico's esports ecosystem.",
    back: "← Back to home",
    eyebrow: "PREL guide",
    heroTitle: "What is Puerto Rico Esports League?",
    heroBody:
      "Puerto Rico Esports League, also known as PREL, is an esports and entertainment platform built from Puerto Rico. Its work combines live events, original content, television, activations, and strategic collaborations around competitive culture.",
    shortTitle: "The short explanation",
    shortBody:
      "PREL does not operate only as a tournament league. The platform also produces content, develops audiovisual formats, works with brands and institutions, and creates experiences that connect community, competition, and entertainment.",
    points: [
      "An esports and entertainment platform based in Puerto Rico",
      "Original content, live events, and television programming",
      "Projects with brands, media, institutions, and community",
      "A growth vision from Puerto Rico toward regional and international markets",
    ],
    sections: [
      {
        title: "What PREL does in practice",
        body: "In practice, PREL develops tournaments, event coverage, content for digital platforms, television-style programming, and collaboration opportunities for brands and partners. That allows it to move across entertainment, competition, and youth culture without getting trapped in a single format.",
      },
      {
        title: "Why it matters within Puerto Rico",
        body: "PREL helps give structure and visibility to Puerto Rico's esports ecosystem. It also functions as a meeting point for players, audiences, brands, media, and institutions that want to take part in the sector's growth in a serious and culturally relevant way.",
      },
      {
        title: "Why it does not feel like a generic project",
        body: "PREL has an identity built from Puerto Rico. That context gives it tone, language, community, and legitimacy. At the same time, its format is designed to grow and connect with opportunities outside the island without losing what makes it recognizable.",
      },
    ],
  },
};

export default function WhatIsPrelPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];

  useEffect(() => {
    document.title = copy.title;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.description);
    setMeta("keywords", copy.keywords);
    setMeta("og:title", copy.title, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "article", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/que-es-prel", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/que-es-prel");

    const schemaId = "prel-what-is-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: copy.heroTitle,
          description: copy.ogDescription,
          author: {
            "@type": "Organization",
            name: "Puerto Rico Esports League",
          },
          publisher: {
            "@type": "Organization",
            name: "Puerto Rico Esports League",
            logo: {
              "@type": "ImageObject",
              url: "https://www.puertoricoesportleague.com/logo.png",
            },
          },
          mainEntityOfPage: "https://www.puertoricoesportleague.com/que-es-prel",
        },
        {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: copy.heroTitle,
              acceptedAnswer: {
                "@type": "Answer",
                text: copy.heroBody,
              },
            },
            {
              "@type": "Question",
              name: lang === "en" ? "Is PREL only a tournament league?" : "¿PREL es solo una liga de torneos?",
              acceptedAnswer: {
                "@type": "Answer",
                text: copy.shortBody,
              },
            },
          ],
        },
      ],
    });
  }, [copy, lang]);

  return (
    <div className="min-h-screen bg-[#06070A] text-white">
      <SiteHeader current="" />

      <main className="max-w-[1200px] mx-auto px-6 xl:px-8 pt-12 pb-20">
        <a href="/" className="text-cyan-300 text-sm hover:underline">{copy.back}</a>
        <p className="mt-8 text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.eyebrow}</p>
        <h1 className="mt-4 text-[clamp(2.7rem,7vw,5.5rem)] font-black leading-[0.92] uppercase">{copy.heroTitle}</h1>
        <p className="mt-6 max-w-4xl text-slate-300 text-lg leading-relaxed">{copy.heroBody}</p>

        <section className="mt-10 border border-white/10 bg-[#111722] p-7 md:p-8">
          <h2 className="text-3xl font-black uppercase">{copy.shortTitle}</h2>
          <p className="mt-4 text-slate-300 leading-relaxed text-lg">{copy.shortBody}</p>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            {copy.points.map((point) => (
              <article key={point} className="border border-white/10 bg-[#0F1220] p-4 text-slate-200">
                {point}
              </article>
            ))}
          </div>
        </section>

        <section className="mt-10 grid gap-6">
          {copy.sections.map((section) => (
            <article key={section.title} className="border border-white/10 bg-[#111722] p-7 md:p-8">
              <h2 className="text-3xl font-black uppercase">{section.title}</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">{section.body}</p>
            </article>
          ))}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
